import { useCallback, useState } from 'react';

type TStatus = 'idle' | 'pending' | 'success' | 'error';

export type TExecuteCallback<T> = () => Promise<{ status: 'success'; data: T } | { status: 'error'; data: null }>;

export const useAsync = <T = unknown, E = Error>(
  asyncFunction: () => Promise<T>,
): [options: { status: TStatus; result: T | null; error: E | null }, execute: TExecuteCallback<T>] => {
  const [result, setResult] = useState<T | null>(null);
  const [status, setStatus] = useState<TStatus>('idle');
  const [error, setError] = useState<E | null>(null);

  const execute = useCallback<TExecuteCallback<T>>(async () => {
    setStatus('pending');
    setResult(null);
    setError(null);

    try {
      const res = await asyncFunction();
      setResult(res);
      setStatus('success');

      return { status: 'success', data: res };
    } catch (error) {
      setError(error);
      setStatus('error');

      return { status: 'error', data: null };
    }
  }, [asyncFunction]);

  return [{ status, result, error }, execute];
};
